import React from 'react';
import {
  IoDocumentTextOutline,
  IoClose,
  IoPrintOutline,
} from 'react-icons/io5';

/**
 * Converts a numeric amount to Turkish text format.
 * Example: 3600 -> "Yalnızca; Üç Bin Altı Yüz TL"
 */
export function numberToTurkishWords(amount) {
  if (amount === undefined || amount === null || isNaN(amount)) return 'Sıfır TL';

  const units = ['', 'Bir', 'İki', 'Üç', 'Dört', 'Beş', 'Altı', 'Yedi', 'Sekiz', 'Dokuz'];
  const tensMap = ['', 'On', 'Yirmi', 'Otuz', 'Kırk', 'Elli', 'Altmış', 'Yetmiş', 'Seksen', 'Doksan'];
  const thousands = ['', 'Bin', 'Milyon', 'Milyar'];

  const num = Math.floor(Math.abs(amount));
  const decimal = Math.round((Math.abs(amount) - num) * 100);

  if (num === 0 && decimal === 0) return 'Sıfır TL';

  function convertGroup(n) {
    let str = '';
    const h = Math.floor(n / 100);
    const t = Math.floor((n % 100) / 10);
    const u = n % 10;

    if (h > 0) {
      if (h === 1) str += 'Yüz';
      else str += units[h] + ' Yüz';
    }
    if (t > 0) {
      str += (str ? ' ' : '') + tensMap[t];
    }
    if (u > 0) {
      str += (str ? ' ' : '') + units[u];
    }
    return str;
  }

  let words = '';
  let tempNum = num;
  let groupIndex = 0;

  if (tempNum === 0) {
    words = 'Sıfır';
  } else {
    while (tempNum > 0) {
      const group = tempNum % 1000;
      if (group > 0) {
        let groupStr = convertGroup(group);
        if (groupIndex === 1 && group === 1) {
          groupStr = 'Bin';
        } else if (groupIndex > 0) {
          groupStr += ' ' + thousands[groupIndex];
        }
        words = groupStr + (words ? ' ' + words : '');
      }
      tempNum = Math.floor(tempNum / 1000);
      groupIndex++;
    }
  }

  let result = `${words} TL`;
  if (decimal > 0) {
    const decStr = convertGroup(decimal);
    result += ` ${decStr} Kuruş`;
  }
  return result;
}

const ReceiptModal = ({ visible, onClose, receiptData }) => {
  if (!visible || !receiptData) return null;

  const {
    studentName = '-',
    installmentNumber = '-',
    date = '-',
    receiptNo = '-',
    paymentMethod = 'Online Ödeme',
    amount,
    amountFormatted,
    amountInWords,
    receivedBy = 'Sistem',
    payments = [],
  } = receiptData;

  // Accurately determine numeric amount from amount prop or amountFormatted string
  let numericAmount = 0;
  if (typeof amount === 'number' && !isNaN(amount)) {
    numericAmount = amount;
  } else if (typeof amount === 'string' && amount.trim() !== '') {
    const parsed = parseFloat(amount.replace(/[^\d.,]/g, '').replace(',', '.'));
    if (!isNaN(parsed)) numericAmount = parsed;
  } else if (amountFormatted) {
    const cleaned = amountFormatted
      .replace(/[^\d.,]/g, '')
      .replace(/\./g, '')
      .replace(',', '.');
    const parsed = parseFloat(cleaned);
    if (!isNaN(parsed)) numericAmount = parsed;
  }

  const formattedAmountText =
    amountFormatted ||
    `₺${numericAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const computedWords = amountInWords || numberToTurkishWords(numericAmount);

  const fallbackReceiptNo =
    receiptNo && receiptNo !== '-'
      ? receiptNo.startsWith('T') || receiptNo.startsWith('#T')
        ? receiptNo
        : `T${String(receiptNo).replace('#', '').padStart(6, '0')}`
      : '-';

  const paymentList =
    payments && payments.length > 0
      ? payments
      : [
          {
            date: date || '-',
            receiptNo: fallbackReceiptNo,
            paymentMethod: paymentMethod || 'Online Ödeme',
            cardInfo: null,
            transactionNo: null,
            authCode: null,
            status: 'Başarılı',
            amount: formattedAmountText,
          },
        ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/75 backdrop-blur-sm overflow-y-auto">
      {/* Overlay click listener */}
      <div className="fixed inset-0 print:hidden" onClick={onClose} />

      {/* Main Modal Window */}
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden z-10 border border-slate-200 flex flex-col max-h-[92vh] my-auto print:max-h-none print:shadow-none print:border-none print:w-full">
        {/* Header Section */}
        <div className="bg-[#1e3a8a] text-white px-5 py-3.5 flex items-center justify-between shadow-sm print:hidden">
          <div className="flex items-center gap-2.5">
            <IoDocumentTextOutline className="text-xl sm:text-2xl text-white" />
            <h3 className="text-base sm:text-lg font-bold tracking-wide">
              Tahsilat Makbuzu Önizleme
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-white/10 text-white transition-colors cursor-pointer"
            aria-label="Kapat"
          >
            <IoClose className="text-2xl" />
          </button>
        </div>

        {/* Scrollable Receipt Body */}
        <div className="p-4 sm:p-6 overflow-y-auto space-y-4 bg-slate-100/60 print:bg-white print:p-0">
          {/* Main Paper Receipt Container */}
          <div className="bg-white rounded-xl p-5 sm:p-6 border border-slate-200 shadow-md space-y-5 print:shadow-none print:border-none">
            {/* Header Section */}
            <div className="flex flex-row justify-between items-start">
              <div>
                <h2 className="text-xl sm:text-2xl font-extrabold text-[#1e3a8a] tracking-wide uppercase">
                  TUNCAY SEYAHAT
                </h2>
                <p className="text-xs sm:text-sm font-semibold text-slate-500 mt-0.5">
                  Tahsilat Makbuzu
                </p>
              </div>
              <div className="text-right space-y-0.5">
                <div className="flex items-center justify-end gap-1.5 text-xs">
                  <span className="font-bold text-[#1e3a8a]">Tarih:</span>
                  <span className="font-medium text-slate-800">{date}</span>
                </div>
                <div className="flex items-center justify-end gap-1.5 text-xs">
                  <span className="font-bold text-[#1e3a8a]">Fiş No:</span>
                  <span className="font-medium text-slate-800">{receiptNo}</span>
                </div>
              </div>
            </div>

            {/* Separator Line */}
            <div className="h-0.5 bg-[#1e3a8a] my-3" />

            {/* Main Info Table */}
            <div className="border border-slate-300 rounded-md overflow-hidden text-xs sm:text-sm mb-4">
              <div className="flex border-b border-slate-200">
                <div className="w-[34%] bg-slate-50 px-3 py-2.5 font-bold text-[#1e3a8a] border-r border-slate-300 flex items-center">
                  Müşteri Adı
                </div>
                <div className="w-[66%] bg-white px-3 py-2.5 text-slate-900 font-medium flex items-center">
                  {studentName}
                </div>
              </div>

              <div className="flex border-b border-slate-200">
                <div className="w-[34%] bg-slate-50 px-3 py-2.5 font-bold text-[#1e3a8a] border-r border-slate-300 flex items-center">
                  Açıklama
                </div>
                <div className="w-[66%] bg-white px-3 py-2.5 text-slate-900 font-medium flex items-center">
                  Taksit Ödemesi (Taksit No: {installmentNumber})
                </div>
              </div>

              <div className="flex border-b border-slate-200">
                <div className="w-[34%] bg-slate-50 px-3 py-2.5 font-bold text-[#1e3a8a] border-r border-slate-300 flex items-center">
                  Tahsilat Şekli
                </div>
                <div className="w-[66%] bg-white px-3 py-2.5 text-slate-900 font-medium flex items-center">
                  {paymentMethod}
                </div>
              </div>

              <div className="flex border-b border-slate-200">
                <div className="w-[34%] bg-slate-50 px-3 py-2.5 font-bold text-[#1e3a8a] border-r border-slate-300 flex items-center">
                  Tutar
                </div>
                <div className="w-[66%] bg-white px-3 py-2.5 text-slate-900 font-extrabold text-sm sm:text-base flex items-center">
                  {formattedAmountText}
                </div>
              </div>

              <div className="flex">
                <div className="w-[34%] bg-slate-50 px-3 py-2.5 font-bold text-[#1e3a8a] border-r border-slate-300 flex items-center">
                  Yazıyla
                </div>
                <div className="w-[66%] bg-white px-3 py-2.5 text-slate-700 italic font-semibold flex items-center">
                  {computedWords}
                </div>
              </div>
            </div>

            {/* Sub Payments Section Header */}
            <div className="mt-4 mb-2">
              <h4 className="text-xs sm:text-sm font-bold text-[#1e3a8a] mb-1.5">
                Bu Taksite Ait Ödemeler (Makbuz Detayı)
              </h4>
              <div className="h-px bg-slate-300" />
            </div>

            {/* Payments Table */}
            <div className="border border-slate-300 rounded-md overflow-hidden text-xs mb-6">
              {/* Table Header */}
              <div className="flex bg-slate-50 py-2 px-2.5 border-b border-slate-300 font-bold text-[#1e3a8a]">
                <div className="flex-[1.3] pr-1">İşlem Tarihi</div>
                <div className="flex-[1.1] pr-1">Makbuz/Fiş No</div>
                <div className="flex-[2.2] pr-1">Ödeme Şekli</div>
                <div className="flex-1 text-center">Durum</div>
                <div className="flex-[1.2] text-right">Tutar</div>
              </div>

              {/* Table Content Rows */}
              {paymentList.map((item, idx) => {
                const isFailed = item.status === 'Başarısız' || item.status === 'failed';
                const cardLabel = item.cardInfo
                  ? item.cardInfo.startsWith('Kart:')
                    ? item.cardInfo
                    : `Kart: ${item.cardInfo}`
                  : null;
                const transLabel = item.transactionNo
                  ? item.transactionNo.startsWith('İşlem No:')
                    ? item.transactionNo
                    : `İşlem No: ${item.transactionNo}`
                  : null;
                const authLabel = item.authCode
                  ? item.authCode.startsWith('Oto. Kodu:')
                    ? item.authCode
                    : `Oto. Kodu: ${item.authCode}`
                  : null;

                return (
                  <div
                    key={idx}
                    className="flex py-2.5 px-2.5 border-b border-slate-200 last:border-b-0 items-start"
                  >
                    <div className="flex-[1.3] text-slate-800 font-medium pr-1">{item.date}</div>
                    <div className="flex-[1.1] text-slate-800 font-medium pr-1">{item.receiptNo}</div>

                    <div className="flex-[2.2] pr-1">
                      <div className="font-semibold text-slate-900">{item.paymentMethod}</div>
                      {cardLabel && <div className="text-[10px] text-slate-500 mt-0.5">{cardLabel}</div>}
                      {transLabel && <div className="text-[10px] text-slate-500 mt-0.5">{transLabel}</div>}
                      {authLabel && <div className="text-[10px] text-slate-500 mt-0.5">{authLabel}</div>}
                    </div>

                    <div className="flex-1 flex items-center justify-center">
                      <span
                        className={`font-bold text-xs ${
                          isFailed ? 'text-red-600' : 'text-emerald-600'
                        }`}
                      >
                        {item.status || 'Başarılı'}
                      </span>
                    </div>

                    <div className="flex-[1.2] text-right font-bold text-slate-900">
                      {item.amount}
                    </div>
                  </div>
                );
              })}

              {/* Table Footer Row - General Total */}
              <div className="flex justify-end items-center bg-slate-50 py-2.5 px-3 border-t border-slate-300">
                <span className="font-bold text-[#1e3a8a] text-xs sm:text-sm mr-2.5">
                  Genel Toplam:
                </span>
                <span className="font-extrabold text-slate-900 text-xs sm:text-sm">
                  {formattedAmountText}
                </span>
              </div>
            </div>
          </div>

          {/* Bottom Action Buttons */}
          <div className="flex gap-3 pt-2 print:hidden">
            <button
              type="button"
              onClick={() => window.print()}
              className="flex-1 bg-[#1e3a8a] hover:bg-[#162d6f] text-white py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors cursor-pointer shadow-sm"
            >
              <IoPrintOutline className="text-lg" />
              Yazdır / İndir
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 sm:flex-initial sm:px-6 bg-slate-200 hover:bg-slate-300 text-slate-800 py-3 rounded-xl font-bold text-sm transition-colors cursor-pointer"
            >
              Kapat
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptModal;
